import { z } from 'zod';
// ─── DataSource ──────────────────────────────────────────────────────────────
export const DataSourceTypeSchema = z.enum([
    'SNOWFLAKE',
    'POSTGRES',
    'REST',
    'FORMANT',
    'S3',
    'OTHER',
]);
export const DataSourceSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    type: DataSourceTypeSchema,
    endpoint: z.string(),
    scope: z.record(z.string(), z.unknown()),
    config: z.record(z.string(), z.unknown()),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateDataSourceInputSchema = z.object({
    name: z.string().min(1).max(200),
    type: DataSourceTypeSchema,
    endpoint: z.string().min(1),
    scope: z.record(z.string(), z.unknown()).default({}),
    creds: z.string().optional(),
    config: z.record(z.string(), z.unknown()).default({}),
});
export const UpdateDataSourceInputSchema = CreateDataSourceInputSchema.partial();
// ─── S3 data source ───────────────────────────────────────────────────────────
//
// For a DataSource of type `S3`: `endpoint` holds the bucket name, `config`
// holds `S3DataSourceConfig`, and the (write-only) `creds` string holds a
// JSON-encoded `S3Creds` payload encrypted at rest via `@cortex/crypto`.
//
// Two auth modes, discriminated on `config.authMode` (kept in *config*, not
// creds, because it and `roleArn` are non-secret and the UI must read them back
// to render/prefill the edit form — only true secrets live in `creds`):
//   • `staticKeys`  — a long-lived IAM access-key pair (also local MinIO dev).
//                     creds = { accessKeyId, secretAccessKey }.
//   • `assumeRole`  — a customer-owned role reached via `sts:AssumeRole` + an
//                     External ID; the runner's ambient identity assumes it, so
//                     no AWS secret of ours is stored. `roleArn`/`sessionName`
//                     live in config; creds = { externalId } (the shared secret).
//                     See `docs/s3-assume-role-design.md`.
export const S3AuthModeSchema = z.enum(['staticKeys', 'assumeRole']);
/** Shape stored in `DataSource.config` when `type === 'S3'`. */
export const S3DataSourceConfigSchema = z
    .object({
    /** AWS region of the bucket, e.g. "us-east-1". */
    region: z.string().min(1),
    /**
     * Optional key-prefix allow-list. When non-empty, every object key the
     * agent reads/writes must start with one of these prefixes — bounds the
     * blast radius of agent access to a dedicated area of the bucket.
     */
    keyPrefix: z.array(z.string()).default([]),
    /** When true, the write tool is refused for this source. Defaults to safe. */
    readOnly: z.boolean().default(true),
    /**
     * Credential mode. Defaults to `staticKeys` so rows written before the
     * auth-mode split (no `authMode` in config) keep resolving as static keys.
     */
    authMode: S3AuthModeSchema.default('staticKeys'),
    /**
     * `assumeRole` only — the role in the CUSTOMER's account we assume, e.g.
     * `arn:aws:iam::<CUSTOMER_ACCT_ID>:role/formant-s3-read`. Non-secret.
     */
    roleArn: z.string().optional(),
    /** `assumeRole` only — optional STS session name for CloudTrail attribution. */
    sessionName: z.string().optional(),
    /**
     * Optional S3-compatible endpoint URL. Leave unset for AWS S3 (the SDK uses
     * the regional default). Set for MinIO / LocalStack / other S3-compatible
     * servers, e.g. "http://localhost:9000" for local MinIO.
     */
    endpoint: z.string().url().optional(),
    /**
     * Use path-style addressing (`endpoint/bucket/key`) instead of virtual-hosted
     * (`bucket.endpoint/key`). Required by most non-AWS servers including MinIO.
     */
    forcePathStyle: z.boolean().optional(),
})
    .superRefine((cfg, ctx) => {
    if (cfg.authMode === 'assumeRole' && !cfg.roleArn) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['roleArn'],
            message: 'roleArn is required when authMode is "assumeRole".',
        });
    }
});
// ── creds: SECRETS ONLY, parsed by mode (discriminant is `config.authMode`) ──
/** `staticKeys` secrets — the IAM access-key pair. */
export const S3StaticKeyCredsSchema = z.object({
    accessKeyId: z.string().min(1),
    secretAccessKey: z.string().min(1),
});
/** `assumeRole` secret — the per-customer External ID (confused-deputy guard). */
export const S3AssumeRoleCredsSchema = z.object({
    externalId: z.string().min(1),
});
/**
 * The write-only `creds` payload (JSON-encoded before encryption). Which arm
 * applies is decided by `config.authMode`, so callers parse the specific schema
 * above; this union exists for typing. A legacy `{ accessKeyId, secretAccessKey }`
 * blob still parses (as the static arm) with no shim needed — the discriminant
 * moved to config, which defaults `authMode` to `staticKeys`.
 */
export const S3CredsSchema = z.union([S3StaticKeyCredsSchema, S3AssumeRoleCredsSchema]);
//# sourceMappingURL=data-source.js.map